import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Convert UTC time string "HH:MM" to local 12-hour format "h:MMpm"
const utcToLocalTime = (utcTime) => {
  if (!utcTime) return null;
  const parts = utcTime.split(':').map(Number);
  const utcH = parts[0];
  const utcM = parts.length > 1 ? parts[1] : 0;
  const now = new Date();
  now.setUTCHours(utcH, utcM, 0, 0);
  let localH = now.getHours();
  const localM = now.getMinutes();
  const ampm = localH >= 12 ? 'pm' : 'am';
  localH = localH % 12 || 12; // Convert 0 → 12, 13 → 1, etc.
  return `${localH}:${String(localM).padStart(2, '0')}${ampm}`;
};

// Format seconds as M:SS (e.g., 45 → "0:45", 127 → "2:07")
const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Explicit tick values for every second 0-60
const DURATION_TICKS = Array.from({ length: 61 }, (_, i) => i);

const STATUS_COLORS = {
  delivered: '#10B981',
  failed: '#EF4444',
  scheduled: '#6366F1',
  pending: '#F59E0B',
  retrying: '#F97316',
  queued: '#8B5CF6',
  sending: '#3B82F6',
  stopped: '#6B7280',
  canceled: '#6B7280'
};

const STATUS_LABELS = {
  delivered: 'Delivered',
  failed: 'Failed',
  scheduled: 'Scheduled',
  pending: 'Pending',
  retrying: 'Retrying',
  queued: 'Queued',
  sending: 'Sending',
  stopped: 'Stopped',
  canceled: 'Canceled'
};

// Close icon SVG
const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Stat card component
const StatCard = ({ label, value, color }) => (
  <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3 text-center`}>
    <div className={`text-2xl font-bold text-${color}-700`}>{value}</div>
    <div className={`text-xs font-medium text-${color}-600 uppercase mt-1`}>{label}</div>
  </div>
);

export default function CampaignDetailsModal({ campaignId, onClose }) {
  const [stats, setStats] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryTotal, setDeliveryTotal] = useState(0);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliveryTotalPages, setDeliveryTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const LIMIT = 25;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaignId}/stats`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const data = await res.json();
      setCampaign(data.campaign);
      setStats(data.stats);
      setTimeline(data.timeline || []);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (searchQuery) params.set('search', searchQuery);
    window.open(`${API_URL}/campaigns/${campaignId}/export-csv?${params.toString()}`, '_blank');
  };

  const fetchDeliveries = useCallback(async () => {
    setDeliveriesLoading(true);
    try {
      const params = new URLSearchParams({ page: deliveryPage, limit: LIMIT });
      if (statusFilter) params.set('status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);

      const res = await fetch(`${API_URL}/campaigns/${campaignId}/deliveries?${params.toString()}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch deliveries');
      const data = await res.json();
      setDeliveries(data.data);
      setDeliveryTotal(data.total);
      setDeliveryTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error fetching deliveries:', err);
    } finally {
      setDeliveriesLoading(false);
    }
  }, [campaignId, deliveryPage, statusFilter, searchQuery]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Build pie chart data from status breakdown
  const pieData = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.by_status || {})
      .map(([status, count]) => ({
        name: STATUS_LABELS[status] || status,
        value: count,
        color: STATUS_COLORS[status] || '#6B7280'
      }))
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  // Y-axis domain: whole numbers, minimum 0-10, expanding if data exceeds 10
  const yAxisDomain = useMemo(() => {
    if (!stats?.duration_distribution?.length) return [0, 10];
    const maxCount = Math.max(...stats.duration_distribution.map(d => d.count));
    return [0, Math.max(10, maxCount)];
  }, [stats]);

  // Format timeline data for bar chart
  const timelineChartData = useMemo(() => {
    return timeline.map(t => ({
      hour: new Date(t.hour).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      delivered: t.delivered,
      failed: t.failed
    }));
  }, [timeline]);

  const getStatusBadge = (status) => {
    const color = STATUS_COLORS[status] || '#6B7280';
    const label = STATUS_LABELS[status] || status;
    return (
      <span
        className="px-2 py-1 text-xs font-medium rounded-full"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
          <p className="text-center mt-4 text-gray-600">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{campaign.name}</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                campaign.type === 'sms'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {campaign.type === 'sms' ? 'SMS' : 'Voice'}
              </span>
              <span className="text-sm text-gray-500">
                Created by {campaign.created_by_name}
              </span>
              <span className="text-sm text-gray-500">
                Dates: {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
              </span>
              {(campaign.daily_start_time || campaign.daily_cutoff_time) && (
                              <span className="text-sm text-gray-500">
                                Daily: {utcToLocalTime(campaign.daily_start_time) || '00:00'} - {utcToLocalTime(campaign.daily_cutoff_time) || '23:59'}
                              </span>
                            )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{stats?.total_recipients || 0}</div>
              <div className="text-xs font-medium text-gray-600 uppercase mt-1">Recipients</div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-indigo-700">{stats?.total_scheduled || 0}</div>
              <div className="text-xs font-medium text-indigo-600 uppercase mt-1">Scheduled</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-700">{stats?.total_delivered || 0}</div>
              <div className="text-xs font-medium text-green-600 uppercase mt-1">Delivered</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-700">{stats?.total_failed || 0}</div>
              <div className="text-xs font-medium text-red-600 uppercase mt-1">Failed</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-700">{stats?.total_retried || 0}</div>
              <div className="text-xs font-medium text-amber-600 uppercase mt-1">Retried</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Donut Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Status Breakdown</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [value, 'Count']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                    />
                    <Legend
                      formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-60 text-gray-400 text-sm">
                  No delivery data yet
                </div>
              )}
            </div>

            {/* Timeline Bar Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Delivery Timeline</h3>
              {timelineChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={timelineChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      dataKey="hour"
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                    />
                    <Legend />
                    <Bar dataKey="delivered" fill="#10B981" name="Delivered" />
                    <Bar dataKey="failed" fill="#EF4444" name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-60 text-gray-400 text-sm">
                  No timeline data yet
                </div>
              )}
            </div>
          </div>

          {/* Voice Call Outcomes — Voice campaigns only */}
          {false && campaign?.type === 'voice' && stats?.voice_status_breakdown && stats.voice_status_breakdown.length > 0 && (
            <div className="mb-6">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Voice Call Outcomes</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  {stats.voice_status_breakdown.map(item => {
                    const colorMap = {
                      'answered': 'bg-green-50 border-green-200 text-green-700',
                      'completed': 'bg-green-50 border-green-200 text-green-700',
                      'busy': 'bg-orange-50 border-orange-200 text-orange-700',
                      'no-answer': 'bg-yellow-50 border-yellow-200 text-yellow-700',
                      'failed': 'bg-red-50 border-red-200 text-red-700',
                      'initiated': 'bg-blue-50 border-blue-200 text-blue-700',
                      'ringing': 'bg-purple-50 border-purple-200 text-purple-700',
                      'canceled': 'bg-gray-50 border-gray-200 text-gray-700',
                    };
                    const bgColor = colorMap[item.status] || 'bg-gray-50 border-gray-200 text-gray-700';
                    return (
                      <div key={item.status} className={`border rounded-lg p-3 text-center ${bgColor}`}>
                        <div className="text-xl font-bold">{item.count}</div>
                        <div className="text-xs font-medium uppercase mt-1">{item.status}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Call Duration Distribution — Voice campaigns only */}
          {campaign?.type === 'voice' && stats?.duration_distribution && stats.duration_distribution.length > 0 && (
            <div className="mb-6">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Call Duration Distribution</h3>
                  {stats.avg_duration && (
                    <span className="text-xs text-gray-500">
                      Avg: {formatDuration(stats.avg_duration)}
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.duration_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      type="category"
                      dataKey="duration"
                      tick={{ fontSize: 8, fill: '#6B7280' }}
                      label={{ value: 'Seconds', position: 'insideBottom', offset: -10, fontSize: 11, fill: '#6B7280' }}
                    />
                    <YAxis
                      domain={yAxisDomain}
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      label={{ value: 'Calls', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#6B7280' }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value} calls`, 'Count']}
                      formatter={(value, name) => name === 'duration' ? [`${value}s`, 'Duration'] : [`${value} calls`, 'Count']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                    />
                    <Bar dataKey="count" fill="#8B5CF6" name="Calls" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Customer Delivery Table */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Customer Deliveries</h3>
            </div>

            {/* Filters */}
            <div className="px-4 py-3 border-b border-gray-100 flex gap-3 flex-wrap items-center">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setDeliveryPage(1); }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
                <option value="scheduled">Scheduled</option>
                <option value="pending">Pending</option>
                <option value="retrying">Retrying</option>
                <option value="queued">Queued</option>
                <option value="sending">Sending</option>
                <option value="stopped">Stopped</option>
                <option value="canceled">Canceled</option>
              </select>

              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setDeliveryPage(1); }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />

              <span className="text-sm text-gray-500 ml-auto">
                {deliveryTotal} total
              </span>
              <button
                onClick={handleExportCSV}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
              >
                Export CSV
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {deliveriesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : deliveries.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No deliveries found
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Phone</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Attempt</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Delivered At</th>
                      {campaign?.type === 'voice' && (
                        <th className="text-center px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Duration</th>
                        )}
                        {campaign?.type === 'voice' && (
                          <th className="text-center px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Call Status</th>
                        )}
                        <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((d) => (
                      <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {d.customer_name || '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                          {d.phone_number}
                        </td>
                        <td className="px-4 py-2">
                          {getStatusBadge(d.status)}
                        </td>
                        <td className="px-4 py-2 text-center text-sm text-gray-600">
                          {d.attempt || 0}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {d.delivered_at
                            ? new Date(d.delivered_at).toLocaleString()
                            : d.failed_at
                            ? new Date(d.failed_at).toLocaleString()
                            : '—'}
                        </td>
                        {campaign?.type === 'voice' && (
                          <td className="px-4 py-2 text-center text-sm text-gray-600 font-mono">
                            {formatDuration(d.call_duration) || '—'}
                          </td>
                        )}
                        {campaign?.type === 'voice' && (
                          <td className="px-4 py-2 text-center text-sm text-gray-600 font-mono">
                            {d.twilio_call_status || '—'}
                          </td>
                        )}
                        <td className="px-4 py-2 text-sm text-red-600 max-w-xs truncate" title={d.error_message || ''}>
                          {d.error_message || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {deliveryTotalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => setDeliveryPage(p => Math.max(1, p - 1))}
                  disabled={deliveryPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ‹ Prev
                </button>
                <span className="text-sm text-gray-600">
                  Page {deliveryPage} of {deliveryTotalPages}
                </span>
                <button
                  onClick={() => setDeliveryPage(p => Math.min(deliveryTotalPages, p + 1))}
                  disabled={deliveryPage >= deliveryTotalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
